import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ToastIntent } from '@fluentui/react-components';
import type { RootState } from '@store/index';

export type { ToastIntent };

export interface SessionNotification {
  id: string;
  message: string;
  intent: ToastIntent;
  timestamp: number;
  read: boolean;
}

export interface NotificationsState {
  items: SessionNotification[];
  lastReadAt: number | null;
}

const MAX_STACK_SIZE = 50;

const initialState: NotificationsState = {
  items: [],
  lastReadAt: null,
};

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `n-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export interface AddNotificationPayload {
  message: string;
  intent: ToastIntent;
  id?: string;
  timestamp?: number;
}

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<AddNotificationPayload>) => {
      const { message, intent, id, timestamp } = action.payload;
      if (!message || message.trim().length === 0) {
        return;
      }
      const entry: SessionNotification = {
        id: id ?? generateId(),
        message,
        intent,
        timestamp: timestamp ?? Date.now(),
        read: false,
      };
      state.items = [entry, ...state.items].slice(0, MAX_STACK_SIZE);
    },
    markAllRead: (state) => {
      state.items.forEach((item) => {
        item.read = true;
      });
      state.lastReadAt = Date.now();
    },
    dismissNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
    clearAll: (state) => {
      state.items = [];
      state.lastReadAt = null;
    },
  },
});

export const { addNotification, markAllRead, dismissNotification, clearAll } =
  notificationsSlice.actions;

export const notificationsReducer = notificationsSlice.reducer;

export const selectAllNotifications = (state: RootState): SessionNotification[] =>
  state.notifications.items;

export const selectUnreadCount = (state: RootState): number =>
  state.notifications.items.reduce((count, item) => (item.read ? count : count + 1), 0);

export const selectHasNotifications = (state: RootState): boolean =>
  state.notifications.items.length > 0;

export const NOTIFICATIONS_MAX_STACK_SIZE = MAX_STACK_SIZE;
